import { reject as rejectProps, isNil, anyPass, isEmpty } from 'ramda';
import { log } from 'log';
import { getBaseDomain } from 'js/utils/url.util';

let isConfigSet = false;
let iapiInitialConfig = {};
let isIntegrationScriptLoadingStarted = false;
let setIntegrationScriptLoaded = () => {};

export const integrationScriptLoadState = new Promise((resolve, reject) => {
    setIntegrationScriptLoaded = (isLoaded, result) => {
        if (isLoaded) {
            resolve(result);
        } else {
            reject(result);
        }

        setIntegrationScriptLoaded = () => {};
    };
});

// region private

// TODO: Use utils/scripts
/**
 * @param {string} src
 * @returns {Promise}
 */
const getScript = src => new Promise((resolve, reject) => {
    const url = new window.URL(src, location.origin);
    const script = document.createElement('script');

    script.setAttribute('async', '');

    script.src = url.href;
    script.onload = (response) => {
        resolve(response);

        // TODO bring this back after Sportium licensee fix.
        // script.remove();
    };
    script.onerror = () => {
        reject(new Error(`Script "${url.href}" loading error.`));

        // TODO bring this back after Sportium licensee fix.
        // script.remove();
    };

    document.body.appendChild(script);
});

/**
 * Get integration.js script from PAS server
 * @returns {Promise}
 */
const getIntegrationScript = () => {
    if (!isConfigSet || isIntegrationScriptLoadingStarted) {
        return integrationScriptLoadState;
    }

    const onStaticPASLoadCallbackName = 'iapiOnPASLoad';
    const pathCollected = `https://${iapiInitialConfig.loginServer}/jswrapper/integration.js.php`
        + '?min=1'
        + '&staticjs=1'
        + `&casino=${iapiInitialConfig.casinoname}`
        + `&onStaticLoad=${onStaticPASLoadCallbackName}`;
    const path = (iapiInitialConfig.ssoPasJsURLs || pathCollected)
        .replace('[superdomain]', getBaseDomain());

    const onGetScriptSuccess = () => {
        if (typeof window.iapiBaseLogin === 'undefined') {
            return;
        }

        Object.assign(window.iapiConf, rejectProps(anyPass([isEmpty, isNil]), iapiInitialConfig));

        setIntegrationScriptLoaded(true);
    };

    const onGetScriptFail = () => {
        setIntegrationScriptLoaded(false, {
            data: {
                errorCode: -1,
                errorMessage: `Network or PAS server failure, can't load: ${path}`
            }
        });
    };

    window[onStaticPASLoadCallbackName] = (event) => {
        if (event.type === 'load') {
            onGetScriptSuccess();
        } else {
            onGetScriptFail();
        }
    };

    isIntegrationScriptLoadingStarted = true;

    // IAPI script was loaded earlier
    if (window.iapiConf) {
        setIntegrationScriptLoaded(true);
    } else if (iapiInitialConfig.loginServer) {
        getScript(path)
            .then(onGetScriptSuccess)
            .catch(onGetScriptFail);
    } else {
        setIntegrationScriptLoaded(false, {
            data: {
                errorCode: -1,
                errorMessage: '"ssoPasDomain" not configured!'
            }
        });
    }

    return integrationScriptLoadState;
};

/**
 * Call PAS action by name
 * @param {string} actionName
 * @param {string} callbackName
 * @param {Array} args
 * @returns {Promise}
 */
export const action = (actionName, callbackName, args) => {
    const description = actionName
        .split('iapi')[1]
        .match(/[A-Z][a-z]+/g)
        .join(' ')
        .toLowerCase();

    log('pasSend', {
        args,
        actionName,
        callbackName,
        description
    });

    return integrationScriptLoadState
        .then(() => new Promise((resolve, reject) => {
            window.iapiSetCallout(window[callbackName], (response) => {
                if (response?.errorCode) {
                    log('pasError', { response, actionName, description });
                    reject({ data: response });

                    return;
                }

                log('pasReceived', {
                    response,
                    actionName,
                    callbackName,
                    description
                });

                resolve({ data: response });
            });

            window[actionName].apply(null, args);
        }));
};

/**
 * Call externalAuth action by name
 * @param {string} actionName
 * @param {Array|Object} [args]
 * @returns {Promise}
 */
const actionExternalAuth = (actionName, args) => {
    if (!window.externalAuth) {
        const errorMessage = 'externalAuth object in externalAuth.js is not defined';

        log('error', errorMessage);

        return Promise.reject({ errorCode: -1, errorMessage });
    }

    return new Promise((resolve, reject) => {
        const description = `externalAuth ${actionName}`;

        log('pasSend', { actionName, args, description });

        window.externalAuth[actionName](args, (response) => {
            if (response && response.errorCode) {
                log('pasError', { actionName, response, description });

                reject(response);

                return;
            }

            log('pasReceived', { actionName, response, description });

            resolve(response);
        });
    });
};

// endregion

// region public

export const init = (app) => {
    iapiInitialConfig = {
        loginServer: app.ssoPasDomain,
        casinoname: app.casinoName,
        clientSkin: app.clientSkin,
        clientType: app.clientType,
        ssoPasJsURLs: app.ssoPasJsURLs,
        ssoPasExternalJsUrl: app.ssoPasExternalJsUrl,
        systemId: 0,
        errorLevel: 1
    };

    isConfigSet = true;

    return getIntegrationScript()
        .catch(error => {
            log('error', error.errorMessage || error);

            throw error;
        });
};

/**
 * Login player to PAS
 * where params are [email|user, pass, realMode, language, systemId, serviceType, rememberMeLogin]
 * @param {Object} options
 * @param {string | null} options.userName
 * @param {string} options.password
 * @param {string} [options.loginMode] 'email', 'userName' or externalToken
 * @param {string} options.languageCode
 * @param {boolean} [options.rememberMeLogin]
 * @returns {Promise}
 */
// swiss override
export const login = (options, resp) => {
    let loginAction = 'iapiLoginAndGetTempToken';
    let callbackName = 'iapiCALLOUT_LOGINANDGETTEMPTOKEN';
    let needsTokenRequest = false;

    switch (options.loginMode) {
        case 'email': {
            loginAction = 'iapiEmailLoginAndGetTempToken';
            break;
        }

        case 'externalToken': {
            loginAction = 'iapiLoginExternalTokenAndGetTempToken';
            break;
        }

        case 'printedTokenCode': {
            // change to iapiLoginPrintedIdTokenCodeAndPinAndGetTempToken once it is done by IMS
            loginAction = 'iapiLoginPrintedIdTokenCodeAndPin';
            callbackName = 'iapiCALLOUT_LOGIN';
            needsTokenRequest = true;
            break;
        }

        default: {
            break;
        }
    }

    if (options.loginMode === 'fidoAuthUi') {
        return action(
            'iapiLoginUsernameExternalToken', 'iapiCALLOUT_LOGIN',
            [resp.username, resp.externalToken, 1, options.languageTwoLetter]);
    }

    let params = [
        options.userName, options.password, 1, options.languageCode
    ];

    if (typeof options.rememberMeLogin === 'boolean') {
        params = params.concat([null, null, options.rememberMeLogin]);
    }

    return action(loginAction, callbackName, params)
        .catch((error) => {
            const responseResult = error.response || error;

            if (responseResult?.data?.sessionValidationData) {
                return responseResult;
            }

            return Promise.reject(error);
        })
        .then(async (response) => {
             if (needsTokenRequest) {
                const tokenResponse = await getTempToken();

                response.data.sessionToken = tokenResponse.data.sessionToken;
            }

            return response;
        });
};

/**
 * Login by link from forgotPassword email.
 * where params are [cryptoToken, language]
 * @param {Object} options
 * @param {string} options.fpActionToken
 * @param {string} options.languageCode
 * @returns {Promise}
 */
export const forgotPassToken = options => action(
    'iapiLoginCryptoToken', 'iapiCALLOUT_LOGIN', [options.fpActionToken, options.languageCode]);

/**
 * Get temporary token
 * where params are [realMode, systemId, serviceType]
 * @returns {Promise}
 */
export const getTempToken = () => action(
    'iapiRequestTemporaryToken', 'iapiCALLOUT_TEMPORARYTOKEN', [1]);

/**
 * Get waiting messages from IMS.
 * where params are [messageTypes, realMode]
 * @param {Object} options
 * @param {string} options.messageTypes
 * @param {number} [options.realMode]
 * @returns {Promise}
 */
export const getWaitingMessages = options => action(
    'iapiGetWaitingMessages', 'iapiCALLOUT_MESSAGES', [options.messageTypes, options.realMode]);

/**
 * Logout from PAS
 * where params are [allSessions, realMode, behavior, invalidateRememberMe]
 * behavior can be one of "this", "all", "hierarchical"
 * @returns {Promise}
 */
export const logout = ({ allSessions = 1, logoutBehavior = 'all' } = {}) => action('iapiLogout', 'iapiCALLOUT_LOGOUT', [allSessions, 1, logoutBehavior, 1]);

/**
 * Change password on PAS
 * where params are [oldPassword, newPassword, changePassword, realMode]
 * @param {Object} options
 * @param {string} [options.oldPassword]
 * @param {string} options.password
 * @returns {Promise}
 */
export const changePassword = options => action(
    'iapiValidatePasswordChange', 'iapiCALLOUT_SESSIONVALIDATION',
    [options.oldPassword, options.password, 1, 1]);

/**
 * Set new terms&conditions on PAS
 * where params are [reference, accept, realMode]
 * @param {Object} options
 * @param {string} options.termVersionReference
 * @param {boolean} options.accept
 * @returns {Promise}
 */
export const setTerms = options => action(
    'iapiValidateTCVersion', 'iapiCALLOUT_SESSIONVALIDATION',
    [options.termVersionReference, options.accept, 1]);

/**
 * where params are [username, email, birthDate, realMode, verificationAnswer,
 * passwordRecoveryDestinationLink, notificationChannels]
 * @param {Object} options
 * @param {string} [options.userName]
 * @param {string} [options.email]
 * @param {string} [options.birthDate]
 * @param {string} [options.verificationAnswer]
 * @param {string} options.passwordRecoveryDestinationLink
 * @param {string} [options.notificationChannels]
 * @returns {Promise}
 */
export const forgotPassword = options => {
    const params = [
        options.userName || '',
        options.email || '',
        options.birthDate || '',
        1,
        options.verificationAnswer || null,
        options.passwordRecoveryDestinationLink
    ];

    if (options.notificationChannels) {
        params.push(options.notificationChannels);
    }

    return action('iapiForgotPassword', 'iapiCALLOUT_FORGOTPASSWORD', params);
};

/**
 * where params are [realMode]
 * @returns {Promise}
 */
export const getLoggedInPlayer = () => action('iapiGetLoggedInPlayer', 'iapiCALLOUT_GETLOGGEDINPLAYER', [1]);

/**
 * where params are [token, realMode, language]
 * @param {Object} options
 * @param {string} options.sessionToken
 * @param {string} options.languageCode
 * @returns {Promise}
 */
export const loginExternalToken = options => action(
    'iapiLoginExternalToken', 'iapiCALLOUT_LOGIN',
    [options.externalToken, 1, options.languageCode]);

/**
 * Two factor validation of token
 * @param {string} token
 * @param {number} isDeviceTrusted
 * @returns {Promise}
 */
export const validateTwoFactorToken = (token, isDeviceTrusted) => action(
    'iapiValidateTwoFactorToken', 'iapiCALLOUT_SESSIONVALIDATION',
    [token, isDeviceTrusted]);

/**
 * Multi factor validation of token
 * @param {string} json
 * @returns {Promise}
 */
export const validateMfaToken = (json) => action(
    'iapiValidateMfaToken', 'iapiCALLOUT_SESSIONVALIDATION',
    [json]);

/**
 * Geolocation validation
 * @param {number | null} latitude
 * @param {number | null} longitude
 * @param {string | null} geoPacket
 * @param {string | null} transactionId
 * @param {string | null} error
 * @param {string | null} [geolocationType]
 * @returns {Promise}
 */
export const validateGeolocation = (latitude, longitude, geoPacket, transactionId, error, geolocationType) => action(
    'iapiValidateGeolocation', 'iapiCALLOUT_SESSIONVALIDATION',
    [latitude, longitude, geoPacket, transactionId, error, geolocationType]);

/**
 * Pin verification
 * @param {string} pin
 * @returns {Promise}
 */
export const validatePin = pin => action('iapiValidatePinToken', 'iapiCALLOUT_SESSIONVALIDATION', [pin]);

/**
 * FitToPlay Token validation
 * @param {boolean} isFitToPlay
 * @returns {Promise}
 */
export const validateFitToPlayToken = isFitToPlay => action(
    'iapiValidateFitToPlayToken', 'iapiCALLOUT_SESSIONVALIDATION',
    [isFitToPlay]);

/**
 * Resend 2FA token upon login
 * @returns {Promise}
 */
export const resendLogin2FAToken = () => action('iapiResendLogin2FAToken', 'iapiCALLOUT_RESENDLOGIN2FATOKEN', []);

export const setIovationBlackbox = blackbox => integrationScriptLoadState
    .then(() => window.iapiSetIovationBlackbox(blackbox));

export const setDeviceId = deviceId => integrationScriptLoadState
    .then(() => window.iapiSetDeviceId(encodeURIComponent(deviceId)));

export const setDeliveryPlatform = deliveryPlatform => integrationScriptLoadState
    .then(() => window.iapiSetDeliveryPlatform(deliveryPlatform));

export const setClientPlatform = clientPlatform => integrationScriptLoadState
    .then(() => window.iapiSetClientPlatform(clientPlatform));

export const setClientType = clientType => integrationScriptLoadState
    .then(() => window.iapiSetClientType(clientType));

export const registrationBankId = (actionToken, callback) => actionExternalAuth('startRegistration', { actionToken })
    .finally(callback);

/**
 * @param {Object} options
 * @param {string} options.actionToken
 * @param {string} options.language
 * @param {Function} callback
 */
export const loginBankId = (options, callback) => actionExternalAuth('login', options)
    .finally(callback);

export const validateSessionBankId = (options, callback) => actionExternalAuth('validateSession', options)
    .finally(callback);

export const cancelBankId = () => actionExternalAuth('cancel');

/**
 * Used in reCaptcha Login flow
 * @param {Object} challenge
 * @param {string} challenge.responseToken - retrieved from CAPTCHA provider
 * @param {string} challenge.operation - type of operation, e.g. "login", "signup", "checkUsernameAvailability"
 * @param {Function} challenge.callback - upon successful completion the callback is to be invoked with an objects that contains "token"
 *     and "expirationTime"
 */
export const validateChallengeAnswer = (challenge) => {
    // load patch with validateChallengeAnswer function
    if (!window.validateChallengeAnswer) {
        const patchPath = `https://${iapiInitialConfig.loginServer}/jswrapper/validateChallengeAnswer.js`;

        getScript(patchPath)
            .then(() => {
                window.validateChallengeAnswer(challenge);
            });
    } else {
        window.validateChallengeAnswer(challenge);
    }
};

/**
 * Set login type
 * @param  {string|Undefined}   type
 * @returns {Deferred}
 */
export const setLoginType = type => getIntegrationScript()
    .then(() => {
        if (window.iapiLoginTypes && window.iapiLoginTypes[type]) {
            window.iapiConf.authenticationType = window.iapiLoginTypes[type];
        } else {
            window.iapiConf && delete window.iapiConf.authenticationType;
        }
    })
    .catch(() => {
        log('error', 'Pas is not ready');

        return Promise.reject({
            data: {
                errorCode: -1,
                errorMessage: 'integration.js for PAS is not defined'
            }
        });
    });

export const getFitToPlaySubmissionRequired = () => {
    return window.iapiConf?.fitToPlaySubmissionRequired === '1';
};

// endregion

// swiss

export const iapiLoginUsernameExternalToken = options => action(
    'iapiLoginUsernameExternalToken', 'iapiCALLOUT_LOGIN',
    [options.user, options.sessionToken, 1, options.languageCode]);
